/**
 * Runs the face deductions (`withDeducedFaces`) for speaker-scene framing and
 * logs what they added, with times a reviewer can check (PLAT-12143). A
 * failure here never stops framing: it falls back to the map it was given.
 */

import { withDeducedFaces } from '@src/services/framing/withDeducedFaces';
import { extractSpeakerIntervals } from '@src/services/framing/speakerIntervalUtils';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { convertTimeToMMSS } from '@src/utility/timeFormat';

import type { TranscriptWord } from '@src/services/framing/speakerIntervalUtils';
import type { FaceDetection } from '@src/services/framing/types';

const logger = LogManager.getLogger('framing:faceDeductions');

/** `count` evenly spaced entries of sorted `values`, first and last included. */
function spreadSample(values: number[], count: number): number[] {
  if (values.length <= count) return values;
  return Array.from(
    { length: count },
    (_, i) => values[Math.round((i * (values.length - 1)) / (count - 1))],
  );
}

export function deduceFacesForFraming(
  readTranscript: () => ReadonlyArray<TranscriptWord> | undefined,
  faceDetections: ReadonlyArray<FaceDetection>,
  speakerMap: Record<string, string[]>,
): Record<string, string[]> {
  try {
    const transcript = readTranscript() ?? [];
    const { map, added, soloShots, leftover } = withDeducedFaces(
      speakerMap,
      transcript,
      faceDetections,
    );
    const at = (faceLabel: string, time: number | undefined) =>
      time === undefined
        ? faceLabel
        : `${faceLabel} @ ${convertTimeToMMSS(time)}`;

    logger.info(
      'Solo-shot deduction ' +
        JSON.stringify({
          added: added.soloShots,
          checkAt: Object.keys(added.soloShots).map(faceLabel =>
            at(faceLabel, soloShots.firstSoloTime[faceLabel]),
          ),
          mapBefore: speakerMap,
          // Where each speaker first talks (media time), to put a name to a label.
          firstTalks: Object.fromEntries(
            extractSpeakerIntervals(transcript)
              .reverse()
              .map(({ speakerLabel, startTime }) => [
                speakerLabel,
                convertTimeToMMSS(startTime),
              ]),
          ),
          frames: soloShots.frames,
          soloFrames: soloShots.soloFrames,
          montageAt: [
            ...new Set(soloShots.montageTimes.map(t => convertTimeToMMSS(t))),
          ],
          votes: soloShots.votes,
        }),
    );
    logger.info(
      'Leftover-face deduction ' +
        JSON.stringify({
          added: added.leftover,
          // Up to 5 moments spread across the video per speaker → face, so a
          // reviewer can see who is talking each time.
          checkTimes: Object.fromEntries(
            Object.entries(leftover.voteTimes).flatMap(([speaker, byFace]) =>
              Object.entries(byFace)
                .filter(([, times]) => times.length >= 5)
                .map(([faceLabel, times]) => [
                  `${speaker} → ${faceLabel}`,
                  spreadSample(times, 5).map(t => convertTimeToMMSS(t)),
                ]),
            ),
          ),
          votes: leftover.votes,
          mapAfter: map,
        }),
    );
    return map;
  } catch (error) {
    logger.warn('Face deduction failed', extractErrorInfo(error));
    return speakerMap;
  }
}
